import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Stack,
  InputAdornment,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Search,
  Visibility,
  LocalShipping,
  CheckCircle,
  Inventory,
  LocalShippingOutlined,
  ViewList,
  ViewModule,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Print,
} from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../components/AuthProvider';
import { useTranslation } from 'react-i18next';

interface OrderItem {
  id: string;
  order_id: string;
  product_id: number;
  product_name: string;
  product_price: number;
  quantity: number;
  total_price: number;
  created_at: string;
  size: string;
  color_name: string;
  item_delivery_status?: string;
  shipping_company?: string;
  tracking_number?: string;
  product?: Product;
}

interface Product {
  product_id: number;
  product_name: string;
  price: number;
  main_images: string[];
  seller_id: string;
}

interface Order {
  id: string;
  order_number: string;
  order_date: string;
  payment_method: string;
  payment_status: string;
  customer_name: string;
  customer_phone: string;
  recipient_name: string;
  recipient_phone: string;
  shipping_address: string;
  special_requests: string;
  total_amount: number;
  status: 'preparing' | 'confirmed' | 'shipping' | 'delivered' | 'cancelled';
  order_items?: OrderItem[];
}

const statusLabel = (status: string, t: (key: string) => string) => {
  switch (status) {
    case 'preparing':
      return <Chip label={t('orders.preparing')} color="warning" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'confirmed':
      return <Chip label={t('orders.packing')} color="success" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'shipping':
      return <Chip label={t('orders.shipping')} color="primary" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'delivered':
      return <Chip label={t('orders.delivered')} color="success" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'cancelled':
      return <Chip label={t('orders.cancelled')} color="default" size="small" sx={{ fontWeight: 700, borderRadius: 1, bgcolor: '#e0e0e0' }} />;
    default:
      return status;
  }
};

// 배송사 목록
const shippingCompanies = [
  '로젠택배',
  '한진택배', 
  'CJ대한통운',
  '우체국택배',
  'GS편의점택배',
  'CU편의점택배',
  '롯데택배',
  '직접입력'
];

// TailAdmin 스타일
const tailAdminStyles = {
  background: {
    light: '#f8fafc',
    dark: '#111827'
  },
  card: {
    light: '#ffffff',
    dark: '#1f2937',
    border: '#e5e7eb',
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
  },
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    accent: '#3b82f6'
  },
  colors: {
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  }
};

export default function AdminOrders() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'product' | 'order'>('product');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  const statusTabs = [
    { label: t('orders.allOrders'), value: 'all' },
    { label: t('orders.preparing'), value: 'preparing' },
    { label: t('orders.packing'), value: 'confirmed' },
    { label: t('orders.shipping'), value: 'shipping' },
    { label: t('orders.cancelled'), value: 'cancelled' },
  ];

  const statusOptions = [
    { value: 'preparing', label: t('orders.preparing') },
    { value: 'confirmed', label: t('orders.packing') },
    { value: 'shipping', label: t('orders.shipping') },
    { value: 'cancelled', label: t('orders.cancelled') },
  ];

  // 배송 관련 상태
  const [shippingCompany, setShippingCompany] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [customShippingCompany, setCustomShippingCompany] = useState('');
  const [showShippingDialog, setShowShippingDialog] = useState(false);
  const [orderToShip, setOrderToShip] = useState<Order | null>(null);

  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (phone: string) => {
    if (!phone || phone === '연락처 정보 없음' || phone === '수령인 연락처 정보 없음') {
      return phone;
    }
    
    // 이미 하이픈이 포함된 경우 그대로 반환
    if (phone.includes('-')) {
      return phone;
    }
    
    // 숫자만 추출
    const numbers = phone.replace(/\D/g, '');
    
    // 11자리 숫자인 경우 000-0000-0000 형식으로 변환
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    
    // 10자리 숫자인 경우 00-0000-0000 형식으로 변환
    if (numbers.length === 10) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    }
    
    // 그 외의 경우 원본 반환
    return phone;
  };

  // 뷰 모드 토글 함수
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'product' ? 'order' : 'product');
  };

  // 주문 확장/축소 토글 함수
  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // 관리자용 주문 데이터 가져오기 (모든 주문 데이터)
  const fetchOrders = async () => {
    if (!user) {
      console.log('사용자 정보가 없습니다.');
      return;
    }
    
    console.log('관리자 권한으로 모든 주문 데이터 조회 시작');
    
    try {
      setLoading(true);
      
      // 1. 모든 order_items 조회 (seller 필터링 제거)
      console.log('1단계: 모든 order_items 조회');
      const { data: orderItems, error: orderItemsError } = await supabase
        .from('order_items')
        .select('*, color_name, size');
      
      if (orderItemsError) {
        console.error('order_items 조회 실패:', orderItemsError);
        return;
      }
      
      console.log('order_items 조회 결과:', orderItems);
      console.log('order_items 개수:', orderItems?.length || 0);

      // 2. 모든 상품 정보 조회
      console.log('2단계: 모든 products 조회');
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*');
      
      if (productsError) {
        console.error('products 조회 실패:', productsError);
        return;
      }
      
      console.log('products 조회 결과:', products);
      console.log('products 개수:', products?.length || 0);

      // 3. 주문 아이템들의 order_id로 주문 정보 조회
      const orderIds = Array.from(new Set(orderItems?.map(item => item.order_id) || []));
      console.log('3단계: 주문 ID 목록:', orderIds);
      console.log('주문 ID 개수:', orderIds.length);
      
      if (orderIds.length === 0) {
        console.log('주문 ID가 없습니다.');
        setOrders([]);
        setLoading(false);
        return;
      }

      // 4. order_id별로 실제 주문 정보와 함께 구성
      console.log('4단계: order_id별로 실제 주문 정보 구성');
      
      // orders 테이블에서 주문 정보 가져오기
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          shipping_request,
          user_id,
          shipping_address_id,
          created_at,
          delivery_status,
          customer_email,
          guest_recipient_name,
          guest_address,
          guest_detail_address,
          guest_postal_code,
          guest_phone
        `)
        .in('id', orderIds);
      
      if (ordersError) {
        console.error('orders 테이블 조회 실패:', ordersError);
      }
      
      console.log('4단계: orders 데이터:', ordersData);
      
      // 사용자 정보 가져오기 (구매자)
      const userIds = ordersData?.map(order => order.user_id).filter(Boolean) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, phone, user_name')
        .in('id', userIds);
      
      if (profilesError) {
        console.error('profiles 테이블 조회 실패:', profilesError);
      }
      
      console.log('4단계: profiles 데이터:', profilesData);
      
      // 배송 주소 정보 가져오기
      const addressIds = ordersData?.map(order => order.shipping_address_id).filter(Boolean) || [];
      const { data: addressesData, error: addressesError } = await supabase
        .from('user_addresses')
        .select('id, full_name, phone_number, address_line1, address_line2, postal_code')
        .in('id', addressIds);
      
      if (addressesError) {
        console.error('user_addresses 테이블 조회 실패:', addressesError);
      }
      
      console.log('4단계: addresses 데이터:', addressesData);
      
      const ordersMap = new Map();
      
      // order_id별로 그룹화하여 실제 데이터로 구성
      orderItems?.forEach(item => {
        if (!ordersMap.has(item.order_id)) {
          const orderData = ordersData?.find(o => o.id === item.order_id);
          const profileData = profilesData?.find(p => p.id === orderData?.user_id);
          const addressData = addressesData?.find(a => a.id === orderData?.shipping_address_id);
          
          // 비회원 여부 판단 (user_id 기준 아닌 shipping_address_id 기준 - 연동 후에도 guest 주소 필드 사용)
          const isGuest = !orderData?.shipping_address_id;

          // 주소 조합
          let fullAddress = '주소 정보 없음';
          if (addressData) {
            const addressParts = [
              addressData.address_line1,
              addressData.address_line2,
              addressData.postal_code
            ].filter(Boolean);
            fullAddress = addressParts.join('\n');
          } else if (isGuest && orderData?.guest_address) {
            const guestParts = [
              orderData.guest_address,
              orderData.guest_detail_address,
              orderData.guest_postal_code
            ].filter(Boolean);
            fullAddress = guestParts.join('\n');
          }

          ordersMap.set(item.order_id, {
            id: item.order_id,
            order_number: item.order_id.slice(0, 8),
            order_date: item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            payment_method: '신용카드', // 하드코딩 (orders 테이블에 없는 경우)
            payment_status: '결제완료', // 하드코딩 (orders 테이블에 없는 경우)
            customer_name: profileData?.user_name || (isGuest ? `비회원 (${orderData?.customer_email || ''})` : `고객 ${item.order_id.slice(0, 8)}`),
            customer_phone: formatPhoneNumber(profileData?.phone || (isGuest ? orderData?.guest_phone || '연락처 정보 없음' : '연락처 정보 없음')),
            recipient_name: addressData?.full_name || (isGuest ? orderData?.guest_recipient_name || `수령인 ${item.order_id.slice(0, 8)}` : `수령인 ${item.order_id.slice(0, 8)}`),
            recipient_phone: formatPhoneNumber(addressData?.phone_number || (isGuest ? orderData?.guest_phone || '수령인 연락처 정보 없음' : '수령인 연락처 정보 없음')),
            shipping_address: fullAddress,
            special_requests: orderData?.shipping_request || '',
            total_amount: 0,
            status: item.item_delivery_status || 'preparing',
            order_items: []
          });
          
          // 디버깅을 위해 실제 delivery_status 값 확인
          console.log(`주문 ${item.order_id}의 delivery_status:`, orderData?.delivery_status);
        }
        
        const order = ordersMap.get(item.order_id);
        const product = products?.find(p => p.product_id === item.product_id);
        const orderItemWithProduct = { 
          ...item, 
          product,
          price: item.product_price,
          quantity: item.quantity,
          size: item.size || '',
          color_name: item.color_name || ''
        };
        order.order_items.push(orderItemWithProduct);
        order.total_amount += item.total_price || (item.product_price * item.quantity);
      });
      
      const ordersWithItems = Array.from(ordersMap.values());
      console.log('구성된 주문 데이터:', ordersWithItems);
      
      // 실제 사용되는 상태값 확인
      const usedStatuses = new Set(ordersWithItems.map(order => order.status));
      console.log('실제 사용되는 상태값들:', Array.from(usedStatuses));
      
      setOrders(ordersWithItems);
    } catch (error) {
      console.error('주문 데이터 가져오기 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleUpdateStatus = async (orderId: string, newStatus: Order['status'], shippingCompany?: string, trackingNumber?: string) => {
    try {
      console.log(`🔄 상태 업데이트 시작: 주문 ${orderId} -> ${newStatus}`);
      
      // 현재 시간
      const now = new Date().toISOString();
      
      // order_items 업데이트 객체 준비
      const updateData: any = {
        item_delivery_status: newStatus  // 새로운 필드 사용
      };
      
      switch (newStatus) {
        case 'confirmed':
          updateData.confirmed_at = now;
          break;
        case 'shipping':
          updateData.prepared_at = now;
          updateData.shipped_at = now;
          if (shippingCompany) updateData.shipping_company = shippingCompany;
          if (trackingNumber) updateData.tracking_number = trackingNumber;
          break;
      }
      
      // order_items 테이블만 업데이트 (orders 테이블 업데이트 제거)
      const { error: itemsError } = await supabase
        .from('order_items')
        .update(updateData)
        .eq('order_id', orderId);
      
      if (itemsError) {
        console.error('❌ order_items 업데이트 실패:', itemsError);
        return;
      }
      
      console.log('✅ order_items 업데이트 성공');
      
      // 로컬 상태 업데이트
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      
      console.log(`✅ 주문 ${orderId} 상태를 ${newStatus}로 업데이트했습니다.`);
      
    } catch (error) {
      console.error('💥 주문 상태 업데이트 실패:', error);
    }
  };


  const handleShipOrder = (order: Order) => {
    setOrderToShip(order);
    setShippingCompany('');
    setTrackingNumber('');
    setCustomShippingCompany('');
    setShowShippingDialog(true);
  };

  const handleConfirmShipping = async () => {
    if (!orderToShip || !trackingNumber.trim()) return;
    
    const finalShippingCompany = shippingCompany === '직접입력' ? customShippingCompany : shippingCompany;
    if (!finalShippingCompany.trim()) return;

    await handleUpdateStatus(orderToShip.id, 'shipping', finalShippingCompany, trackingNumber);
    setShowShippingDialog(false);
    setOrderToShip(null);
  };

  // 프린트 기능
  const handlePrintOrder = (order: Order) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>주문서 - ${order.order_number}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .section { 
              margin-bottom: 25px; 
            }
            .section-title { 
              font-weight: bold; 
              font-size: 18px;
              margin-bottom: 10px;
              color: #333;
              border-left: 4px solid #007bff;
              padding-left: 10px;
            }
            .info-grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 15px; 
            }
            .info-item { 
              margin-bottom: 8px; 
            }
            .info-label { 
              font-weight: bold; 
              color: #555; 
            }
            .info-value { 
              margin-left: 10px; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 10px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
            }
            .total { 
              text-align: right; 
              font-weight: bold; 
              font-size: 18px;
              margin-top: 15px;
              padding: 10px;
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>주 문 서</h1>
            <h2>주문번호: ${order.order_number}</h2>
          </div>

          <div class="section">
            <div class="section-title">주문 정보</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">주문번호:</span>
                <span class="info-value">${order.order_number}</span>
              </div>
              <div class="info-item">
                <span class="info-label">주문일시:</span>
                <span class="info-value">${order.order_date}</span>
              </div>
              <div class="info-item">
                <span class="info-label">결제수단:</span>
                <span class="info-value">${order.payment_method}</span>
              </div>
              <div class="info-item">
                <span class="info-label">결제상태:</span>
                <span class="info-value">${order.payment_status}</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">구매자 정보</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">이름:</span>
                <span class="info-value">${order.customer_name}</span>
              </div>
              <div class="info-item">
                <span class="info-label">연락처:</span>
                <span class="info-value">${order.customer_phone}</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">배송지 정보</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">수령인:</span>
                <span class="info-value">${order.recipient_name}</span>
              </div>
              <div class="info-item">
                <span class="info-label">수령인 연락처:</span>
                <span class="info-value">${order.recipient_phone}</span>
              </div>
              <div class="info-item" style="grid-column: 1 / -1;">
                <span class="info-label">배송주소:</span>
                <span class="info-value" style="white-space: pre-line;">${order.shipping_address}</span>
              </div>
              <div class="info-item" style="grid-column: 1 / -1;">
                <span class="info-label">요청사항:</span>
                <span class="info-value">${order.special_requests || '없음'}</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">주문 상품</div>
            <table>
              <thead>
                <tr>
                  <th>상품명</th>
                  <th>옵션</th>
                  <th>수량</th>
                  <th>단가</th>
                  <th>소계</th>
                </tr>
              </thead>
              <tbody>
                ${order.order_items?.map(item => `
                  <tr>
                    <td>${item.product?.product_name || item.product_name}</td>
                    <td>${item.color_name && item.size ? `${item.color_name} / ${item.size}` : (item.color_name || item.size || '-')}</td>
                    <td>${item.quantity}</td>
                    <td>${item.product_price.toLocaleString()}원</td>
                    <td>${item.total_price.toLocaleString()}원</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="total">
              총 결제금액: ${order.total_amount.toLocaleString()}원
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.order_items?.some(item =>
                           item.product?.product_name.toLowerCase().includes(searchTerm.toLowerCase())
                         );
    const matchesStatus =
      tab === 0 ||
      (tab === 1 && order.status === 'preparing') ||
      (tab === 2 && order.status === 'confirmed') ||
      (tab === 3 && order.status === 'shipping') ||
      (tab === 4 && order.status === 'cancelled');
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // 주문 날짜 순서대로 정렬 (최신순)
    return new Date(b.order_date).getTime() - new Date(a.order_date).getTime();
  });

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // 필터 변경 시 첫 페이지로 이동
  useEffect(() => {
    setPage(1);
  }, [searchTerm, tab]);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: tailAdminStyles.background.light,
      p: 3
    }}>
      <Typography 
        variant="h4" 
        fontWeight={700} 
        mb={3} 
        sx={{ color: tailAdminStyles.text.primary }}
      >
        Orders Management
      </Typography>
      
      <Paper sx={{ 
        backgroundColor: tailAdminStyles.card.light,
        borderRadius: 2,
        boxShadow: tailAdminStyles.card.shadow,
        border: `1px solid ${tailAdminStyles.card.border}`,
        p: 3, 
        mb: 3 
      }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            placeholder="Search by customer or order number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: tailAdminStyles.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: 240,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: tailAdminStyles.card.light,
                '& fieldset': {
                  borderColor: tailAdminStyles.card.border
                },
                '&:hover fieldset': {
                  borderColor: tailAdminStyles.colors.primary
                }
              }
            }}
            size="small"
          />
          
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ 
              minHeight: 40, 
              ml: { sm: 2 },
              '& .MuiTab-root': {
                color: tailAdminStyles.text.secondary,
                '&.Mui-selected': {
                  color: tailAdminStyles.colors.primary
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: tailAdminStyles.colors.primary
              }
            }}
            TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
          >
            {statusTabs.map((t) => (
              <Tab key={t.value} label={t.label} sx={{ fontWeight: 700, minHeight: 40 }} />
            ))}
          </Tabs>
          
          <Box flex={1} />
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newViewMode) => {
              if (newViewMode !== null) {
                setViewMode(newViewMode);
              }
            }}
            sx={{ 
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 100,
                border: `1px solid ${tailAdminStyles.card.border}`,
                color: tailAdminStyles.text.primary,
                '&.Mui-selected': {
                  backgroundColor: tailAdminStyles.colors.primary,
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: tailAdminStyles.colors.primary,
                  }
                }
              }
            }}
          >
            <ToggleButton value="product">
              <ViewList sx={{ mr: 1 }} />
              상품별
            </ToggleButton>
            <ToggleButton value="order">
              <ViewModule sx={{ mr: 1 }} />
              주문별
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>
      
      <TableContainer component={Paper} sx={{
        backgroundColor: tailAdminStyles.card.light,
        borderRadius: 2,
        boxShadow: tailAdminStyles.card.shadow,
        border: `1px solid ${tailAdminStyles.card.border}`
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {viewMode === 'product' ? (
                <>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>주문번호</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>주문일시</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>상품명</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>옵션</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>갯수</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>상태</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>액션</TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>주문번호</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>주문일시</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>구매자</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>상품 수</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>총 금액</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>상태</TableCell>
                  <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>액션</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress sx={{ color: tailAdminStyles.colors.primary }} />
                </TableCell>
              </TableRow>
            ) : paginatedOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                  <Typography color={tailAdminStyles.text.secondary}>주문이 없습니다.</Typography>
                </TableCell>
              </TableRow>
            ) : viewMode === 'product' ? (
              // 상품별 보기: 각 주문 아이템을 개별 행으로 표시
              paginatedOrders.map((order) => 
                order.order_items?.map((item, index) => (
                  <TableRow key={`${order.id}-${index}`} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                    <TableCell sx={{ fontWeight: 600, color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{order.order_number}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{order.order_date}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{item.product?.product_name || 'Unknown Product'}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.secondary}>
                        {item.color_name && item.size ? `${item.color_name} / ${item.size}` : 
                         item.color_name || item.size || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{item.quantity}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {statusLabel(order.status, t)}
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={order.status}
                            onChange={(e) => handleUpdateStatus(order.id, e.target.value as Order['status'])}
                            variant="outlined"
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              '& .MuiSelect-select': {
                                padding: '2px 8px',
                                backgroundColor: tailAdminStyles.card.light,
                                color: tailAdminStyles.text.primary
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: tailAdminStyles.card.border
                              }
                            }}
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewOrder(order)}
                          sx={{ 
                            textTransform: 'none', 
                            fontWeight: 500, 
                            color: tailAdminStyles.text.primary,
                            '&:hover': {
                              bgcolor: '#f3f4f6'
                            }
                          }}
                        >
                          상세보기
                        </Button>
                        {order.status === 'preparing' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => handleUpdateStatus(order.id, 'confirmed')}
                            sx={{ 
                              textTransform: 'none', 
                              fontWeight: 500,
                              backgroundColor: tailAdminStyles.colors.success,
                              '&:hover': {
                                backgroundColor: '#059669'
                              }
                            }}
                          >
                            접수
                          </Button>
                        )}
                        {order.status === 'confirmed' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<LocalShippingOutlined />}
                            onClick={() => handleShipOrder(order)}
                            sx={{ 
                              textTransform: 'none', 
                              fontWeight: 500,
                              backgroundColor: tailAdminStyles.colors.primary,
                              '&:hover': { 
                                backgroundColor: '#2563eb' 
                              }
                            }}
                          >
                            배송
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )
            ) : (
              // 주문별 보기: 각 주문을 하나의 행으로 표시
              paginatedOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <TableRow sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                    <TableCell sx={{ fontWeight: 600, color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{order.order_number}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{order.order_date}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{order.customer_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{order.order_items?.length || 0}개</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Typography color={tailAdminStyles.text.primary}>{(order.total_amount).toLocaleString()}원</Typography>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {statusLabel(order.status, t)}
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={order.status}
                            onChange={(e) => handleUpdateStatus(order.id, e.target.value as Order['status'])}
                            variant="outlined"
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              '& .MuiSelect-select': {
                                padding: '2px 8px',
                                backgroundColor: tailAdminStyles.card.light,
                                color: tailAdminStyles.text.primary
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: tailAdminStyles.card.border
                              }
                            }}
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewOrder(order)}
                          sx={{ 
                            textTransform: 'none', 
                            fontWeight: 500, 
                            color: tailAdminStyles.text.primary,
                            '&:hover': {
                              bgcolor: '#f3f4f6'
                            }
                          }}
                        >
                          상세보기
                        </Button>
                        {order.status === 'preparing' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => handleUpdateStatus(order.id, 'confirmed')}
                            sx={{ 
                              textTransform: 'none', 
                              fontWeight: 500,
                              backgroundColor: tailAdminStyles.colors.success,
                              '&:hover': {
                                backgroundColor: '#059669'
                              }
                            }}
                          >
                            접수
                          </Button>
                        )}
                        {order.status === 'confirmed' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<LocalShippingOutlined />}
                            onClick={() => handleShipOrder(order)}
                            sx={{ 
                              textTransform: 'none', 
                              fontWeight: 500,
                              backgroundColor: tailAdminStyles.colors.primary,
                              '&:hover': { 
                                backgroundColor: '#2563eb' 
                              }
                            }}
                          >
                            배송
                          </Button>
                        )}
                        {/* 확장/축소 버튼 */}
                        <IconButton
                          size="small"
                          onClick={() => toggleOrderExpansion(order.id)}
                          sx={{ ml: 1, color: tailAdminStyles.text.primary }}
                        >
                          {expandedOrders.has(order.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {/* 확장된 상품 정보 */}
                  {expandedOrders.has(order.id) && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                        <Box sx={{ bgcolor: '#f8fafc', p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: tailAdminStyles.text.primary }}>
                            주문 상품 목록
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                                <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>상품명</TableCell>
                                <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>옵션</TableCell>
                                <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>갯수</TableCell>
                                <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>가격</TableCell>
                                <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>소계</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {order.order_items?.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                                    <Typography color={tailAdminStyles.text.primary}>{item.product?.product_name || item.product_name}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                                    <Typography color={tailAdminStyles.text.secondary}>
                                      {item.color_name && item.size ? `${item.color_name} / ${item.size}` : 
                                       item.color_name || item.size || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                                    <Typography color={tailAdminStyles.text.primary}>{item.quantity}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                                    <Typography color={tailAdminStyles.text.primary}>{(item.product_price).toLocaleString()}원</Typography>
                                  </TableCell>
                                  <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                                    <Typography color={tailAdminStyles.text.primary}>{(item.total_price).toLocaleString()}원</Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* 주문 상세 다이얼로그 */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth 
        PaperProps={{ 
          sx: {
            backgroundColor: tailAdminStyles.card.light,
            borderRadius: 2
          } 
        }}
      >
        <DialogTitle sx={{ color: tailAdminStyles.text.primary }}>
          주문 상세 - {selectedOrder?.order_number}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* 주문 정보 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={tailAdminStyles.text.primary}>
                  주문 정보
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>주문번호</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.order_number}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>주문일시</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.order_date}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>결제 수단</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.payment_method}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>결제 상태</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.payment_status}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* 구매자 정보 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={tailAdminStyles.text.primary}>
                  구매자 정보
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>이름</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.customer_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>연락처</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{formatPhoneNumber(selectedOrder.customer_phone)}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* 배송지 정보 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={tailAdminStyles.text.primary}>
                  배송지 정보
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>수령인</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.recipient_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>수령인 연락처</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{formatPhoneNumber(selectedOrder.recipient_phone)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>주소</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary} sx={{ whiteSpace: 'pre-line' }}>{selectedOrder.shipping_address}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={tailAdminStyles.text.secondary}>요청사항</Typography>
                    <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.special_requests || '없음'}</Typography>
                  </Grid>
                  {selectedOrder.order_items && selectedOrder.order_items.length > 0 && selectedOrder.order_items[0].shipping_company && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color={tailAdminStyles.text.secondary}>택배회사</Typography>
                        <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.order_items[0].shipping_company}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color={tailAdminStyles.text.secondary}>송장번호</Typography>
                        <Typography variant="body1" color={tailAdminStyles.text.primary}>{selectedOrder.order_items[0].tracking_number || '미등록'}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>

              {/* 주문 상품 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={tailAdminStyles.text.primary}>
                  주문 상품
                </Typography>
                <TableContainer component={Paper} sx={{
                  backgroundColor: tailAdminStyles.card.light,
                  borderRadius: 2,
                  boxShadow: tailAdminStyles.card.shadow,
                  border: `1px solid ${tailAdminStyles.card.border}`
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>상품명</TableCell>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>옵션</TableCell>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>갯수</TableCell>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>가격</TableCell>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>소계</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.order_items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                            <Typography color={tailAdminStyles.text.primary}>{item.product?.product_name || 'Unknown Product'}</Typography>
                          </TableCell>
                          <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                            <Typography color={tailAdminStyles.text.secondary}>
                              {item.color_name && item.size ? `${item.color_name} / ${item.size}` : 
                               item.color_name || item.size || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                            <Typography color={tailAdminStyles.text.primary}>{item.quantity}</Typography>
                          </TableCell>
                          <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                            <Typography color={tailAdminStyles.text.primary}>{(item.product_price).toLocaleString()}원</Typography>
                          </TableCell>
                          <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                            <Typography color={tailAdminStyles.text.primary}>{(item.total_price).toLocaleString()}원</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="h6" color={tailAdminStyles.text.primary}>
                    총 결제금액: {(selectedOrder.total_amount).toLocaleString()}원
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: tailAdminStyles.text.secondary }}>{t("common.close")}</Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => selectedOrder && handlePrintOrder(selectedOrder)}
            sx={{
              borderColor: tailAdminStyles.colors.primary,
              color: tailAdminStyles.colors.primary,
              '&:hover': {
                borderColor: '#2563eb',
                color: '#2563eb',
                bgcolor: 'transparent'
              }
            }}
          >
            인쇄
          </Button>
          {selectedOrder && selectedOrder.status === 'preparing' && (
            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={() => {
                handleUpdateStatus(selectedOrder.id, 'confirmed');
                setOpenDialog(false);
              }}
              sx={{
                backgroundColor: tailAdminStyles.colors.success,
                '&:hover': {
                  backgroundColor: '#059669'
                }
              }}
            >
              주문 접수
            </Button>
          )}
          {selectedOrder && selectedOrder.status === 'confirmed' && (
            <Button
              variant="contained"
              startIcon={<LocalShippingOutlined />}
              onClick={() => {
                handleShipOrder(selectedOrder);
                setOpenDialog(false);
              }}
              sx={{
                backgroundColor: tailAdminStyles.colors.primary,
                '&:hover': {
                  backgroundColor: '#2563eb'
                }
              }}
            >
              배송 시작
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 배송 다이얼로그 */}
      <Dialog 
        open={showShippingDialog} 
        onClose={() => setShowShippingDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 
          sx: {
            backgroundColor: tailAdminStyles.card.light,
            borderRadius: 2
          } 
        }}
      >
        <DialogTitle sx={{ color: tailAdminStyles.text.primary }}>배송 정보 입력</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* 택배사 선택 */}
            <FormControl fullWidth margin="normal">
              <Typography variant="body2" gutterBottom color={tailAdminStyles.text.primary}>택배사</Typography>
              <Select
                value={shippingCompany}
                onChange={(e) => setShippingCompany(e.target.value)}
                displayEmpty
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: tailAdminStyles.card.border
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: tailAdminStyles.colors.primary
                  }
                }}
              >
                <MenuItem value="">택배사를 선택해주세요</MenuItem>
                {shippingCompanies.map((company) => (
                  <MenuItem key={company} value={company}>
                    {company}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 직접입력 선택 시 택배사명 입력 */}
            {shippingCompany === '직접입력' && (
              <TextField
                fullWidth
                margin="normal"
                label="택배사명"
                value={customShippingCompany}
                onChange={(e) => setCustomShippingCompany(e.target.value)}
                placeholder="택배사명을 입력해주세요"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: tailAdminStyles.card.border
                    },
                    '&:hover fieldset': {
                      borderColor: tailAdminStyles.colors.primary
                    }
                  }
                }}
              />
            )}

            {/* 송장번호 입력 */}
            <TextField
              fullWidth
              margin="normal"
              label="송장번호"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="송장번호를 입력해주세요"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: tailAdminStyles.card.border
                  },
                  '&:hover fieldset': {
                    borderColor: tailAdminStyles.colors.primary
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowShippingDialog(false)}
            sx={{ color: tailAdminStyles.text.secondary }}
          >
            취소
          </Button>
          <Button 
            onClick={handleConfirmShipping}
            variant="contained"
            disabled={!shippingCompany || !trackingNumber.trim() || (shippingCompany === '직접입력' && !customShippingCompany.trim())}
            sx={{
              backgroundColor: tailAdminStyles.colors.primary,
              '&:hover': {
                backgroundColor: '#2563eb'
              },
              '&:disabled': {
                backgroundColor: tailAdminStyles.text.secondary
              }
            }}
          >
            배송 시작
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 